import { Entity } from '@backstage/catalog-model';
import {
  DefaultEntityFilters,
  EntityFilter,
} from '@backstage/plugin-catalog-react';

/**
 * Relation types on an `aws-account` Resource that point at a System:
 *   - `partOf` — the primary System (from the Resource's `spec.system`);
 *   - `dependencyOf` — additional Systems for a shared account (from
 *     `spec.dependencyOf`).
 */
const SYSTEM_RELATION_TYPES = ['partOf', 'dependencyOf'];

/** The System entity refs an aws-account Resource is linked to, lower-cased. */
export function systemRefsOf(entity: Entity): string[] {
  return (entity.relations ?? [])
    .filter(r => SYSTEM_RELATION_TYPES.includes(r.type))
    .map(r => r.targetRef.toLowerCase())
    .filter(ref => ref.startsWith('system:'));
}

/**
 * The members of a comma-joined annotation (the owner-role annotations are a
 * single comma-separated *string* of `user:default/*` refs and/or raw emails),
 * trimmed, lower-cased, and de-blanked.
 */
export function annotationMembers(entity: Entity, annotationKey: string): string[] {
  const raw = entity.metadata.annotations?.[annotationKey] ?? '';
  return raw
    .split(',')
    .map(s => s.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * Multi-select System filter over an aws-account Resource's `partOf` /
 * `dependencyOf` relations. Selecting multiple Systems is OR (match any); the
 * AND across the different filters is applied by the entity-list context.
 */
export class AwsAccountSystemFilter implements EntityFilter {
  readonly values: string[];
  constructor(values: string[]) {
    this.values = values.map(v => v.toLowerCase());
  }
  filterEntity(entity: Entity): boolean {
    if (this.values.length === 0) return true;
    const refs = systemRefsOf(entity);
    return this.values.some(v => refs.includes(v));
  }
}

/**
 * Multi-select filter over a comma-joined owner-role annotation
 * (`temabit.com/business-owners`, `temabit.com/technical-owners`, or
 * `temabit.com/project-managers`). Splits the annotation string and tests
 * membership; multiple selected values are OR.
 */
export class AwsAccountAnnotationCsvFilter implements EntityFilter {
  readonly annotationKey: string;
  readonly values: string[];
  constructor(annotationKey: string, values: string[]) {
    this.annotationKey = annotationKey;
    this.values = values.map(v => v.toLowerCase());
  }
  filterEntity(entity: Entity): boolean {
    if (this.values.length === 0) return true;
    const members = annotationMembers(entity, this.annotationKey);
    return this.values.some(v => members.includes(v));
  }
}

/** The custom-filter slots added on top of the stock catalog filters for D1. */
export type AwsAccountFilters = DefaultEntityFilters & {
  awsSystem?: AwsAccountSystemFilter;
  awsBusinessOwner?: AwsAccountAnnotationCsvFilter;
  awsTechOwner?: AwsAccountAnnotationCsvFilter;
  awsProjectManager?: AwsAccountAnnotationCsvFilter;
};
